import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Shield, MessageSquare, Users, AlertTriangle, Check, X, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityGuidelinesPage = () => {
  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuidelines();
  }, []);

  const fetchGuidelines = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community/guidelines`);
      setGuidelines(response.data.guidelines);
    } catch (error) {
      console.error('Failed to fetch guidelines:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="text-center py-12 text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="guidelines-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 mb-4">
            <Shield className="h-8 w-8 text-forest" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-forest mb-2">
            {guidelines?.title || 'Community Standards'}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {guidelines?.intro}
          </p>
        </div>

        {/* Core Rules */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Heart className="h-5 w-5 text-terracotta" />
              Our Community Values
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Sister Diamonds is built on trust, support, and genuine connection. 
              These guidelines help us maintain a safe space for all members.
            </p>
            <ul className="space-y-3">
              {guidelines?.rules?.map((rule, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="mt-1 h-5 w-5 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-terracotta" />
                  </div>
                  <span className="text-gray-700">{rule}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* DM Policy */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-forest" />
              {guidelines?.dm_policy?.title || 'Direct Messaging Policy'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              To protect our community from unwanted solicitation, we use a consent-based messaging system.
            </p>
            <ul className="space-y-3 mb-6">
              {guidelines?.dm_policy?.rules?.map((rule, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="mt-1 h-5 w-5 rounded-full bg-forest/10 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-forest" />
                  </div>
                  <span className="text-gray-700">{rule}</span>
                </li>
              ))}
            </ul>

            <div className="p-4 bg-sand/50 rounded-xl">
              <p className="font-medium text-forest mb-2">How to Unlock DMs:</p>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-white rounded-lg text-center">
                  <div className="text-2xl font-bold text-terracotta">3</div>
                  <div className="text-gray-600">Posts or Comments</div>
                </div>
                <div className="flex items-center justify-center text-gray-400 font-medium">OR</div>
                <div className="p-3 bg-white rounded-lg text-center">
                  <div className="text-2xl font-bold text-terracotta">7</div>
                  <div className="text-gray-600">Days Active</div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-3 text-center">
                Joining a Community Circle also unlocks DM access!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Business Spaces */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Where Business Talk is Allowed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Business conversations are welcome in designated spaces when they are mutual and invited.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">Allowed Spaces</span>
                </div>
                <ul className="space-y-2 text-sm text-green-700">
                  {guidelines?.business_spaces?.allowed?.map((space, index) => (
                    <li key={index}>• {space}</li>
                  ))}
                </ul>
              </div>
              
              <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="flex items-center gap-2 mb-3">
                  <X className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-800">Not Allowed</span>
                </div>
                <ul className="space-y-2 text-sm text-red-700">
                  {guidelines?.business_spaces?.not_allowed?.map((space, index) => (
                    <li key={index}>• {space}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reporting */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              If You See Something, Say Something
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              If you experience unwanted solicitation, feel unsafe, or notice behavior that 
              violates our guidelines, please use the Report button found on profiles, posts, and messages.
            </p>
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
              <p className="text-sm text-amber-800">
                <strong>Your reports are confidential.</strong> When you report someone, 
                their content is hidden from your view, and our team reviews the situation. 
                We take every report seriously to keep our community safe.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Message */}
        <div className="text-center mt-8 p-6 bg-forest/5 rounded-xl">
          <p className="text-forest italic">
            &ldquo;Safety is not created by surveillance — it&apos;s created by structure, clarity, and consistent boundaries.&rdquo;
          </p>
          <p className="text-sm text-gray-500 mt-2">— Sister Diamonds Community</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CommunityGuidelinesPage;
